import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PLAN_INFO } from '../config/tier-config';
import { useAuth } from '../context/auth-context';
import './pricing-page.css';

// Pricing page only markets Free + Basic for now, matching the landing page
// (src/pages/landing-page.js's LANDING_PLAN_KEYS) — Pro/Elite still exist in
// the product (Settings, tier-config.js, alert routing all still use the
// full PLAN_INFO), this just trims what's shown here to match.
const PRICING_PAGE_PLAN_KEYS = ['free', 'basic'];

function PlanCard({ planKey, plan, currentTier, onSelect, isLoading }) {
  const isCurrentPlan = planKey === currentTier;

  const rankMap = { free: 0, basic: 1, pro: 2, elite: 3 };
  const isDowngrade = currentTier && rankMap[planKey] < rankMap[currentTier];
  const isUpgrade   = currentTier && rankMap[planKey] > rankMap[currentTier];

  let btnLabel = 'Get started free';
  if (isCurrentPlan)  btnLabel = 'Current plan';
  else if (isDowngrade) btnLabel = 'Downgrade';
  else if (isUpgrade)   btnLabel = 'Upgrade to ' + plan.name;
  else if (planKey === 'free') btnLabel = 'Get started free';

  return (
    <div className={'pp-card' + (isCurrentPlan ? ' pp-card-current' : '')}>
      {isCurrentPlan && <div className="pp-current-badge">Your Plan</div>}
      <div className="pp-plan-name">{plan.name}</div>
      <div className="pp-price">{plan.label}</div>
      <ul className="pp-features">
        {plan.features.map(function(f, i) {
          return (
            <li key={i}>
              <span className="pp-check">&#10003;</span>
              <span>{f}</span>
            </li>
          );
        })}
      </ul>
      <button
        className={'pp-cta' + (isCurrentPlan ? ' pp-cta-disabled' : '')}
        onClick={function() { if (!isCurrentPlan) onSelect(planKey); }}
        disabled={isCurrentPlan || isLoading}
      >
        {isLoading ? 'Redirecting...' : btnLabel}
      </button>
    </div>
  );
}

function PricingPage() {
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const { user, tier }  = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const upgradeTarget = searchParams.get('upgrade'); // e.g. 'pro' or 'elite'

  async function handlePlanSelect(planKey) {
    if (planKey === 'free') {
      if (user) {
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
      return;
    }

    if (!user) {
      navigate('/login?plan=' + planKey);
      return;
    }

    // Kick off Stripe checkout
    setIsLoading(true);
    setCheckoutError(null);
    try {
      const res = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: user.uid, plan: planKey }),
      });
      if (!res.ok) {
        const err = await res.json().catch(function() { return {}; });
        throw new Error(err.error || 'Failed to create checkout session');
      }
      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setCheckoutError(err.message);
      setIsLoading(false);
    }
  }

  return (
    <div className="pricing-page">

      <div className="pp-header">
        <button className="pp-back-btn" onClick={function() { navigate(user ? '/dashboard' : '/'); }}>
          &#8592; {user ? 'Back to Dashboard' : 'Back'}
        </button>
      </div>

      <div className="pp-hero">
        {upgradeTarget && (
          <div className="pp-upgrade-notice">
            <span className="pp-lock-icon">&#128274;</span>
            {PRICING_PAGE_PLAN_KEYS.includes(upgradeTarget)
              ? 'Upgrade to access this strategy'
              : "That strategy needs a plan we're not offering yet — Pro and Elite are coming soon"}
          </div>
        )}
        <h1 className="pp-title">Simple, transparent pricing</h1>
        <p className="pp-sub">Start free. Upgrade when you're ready to unlock more strategies.</p>
      </div>

      {checkoutError && (
        <div className="pp-error">
          <strong>Error:</strong> {checkoutError}
        </div>
      )}

      <div className="pp-cards">
        {Object.entries(PLAN_INFO)
          .filter(function(entry) { return PRICING_PAGE_PLAN_KEYS.includes(entry[0]); })
          .map(function(entry) {
            var planKey = entry[0];
            var plan    = entry[1];
            return (
              <PlanCard
                key={planKey}
                planKey={planKey}
                plan={plan}
                currentTier={tier}
                onSelect={handlePlanSelect}
                isLoading={isLoading}
              />
            );
          })}
      </div>
      <p className="pp-coming-soon">Pro and Elite plans are coming soon.</p>

      <div className="pp-guarantee">
        <div className="pp-guarantee-icon">&#128274;</div>
        <div>
          <div className="pp-guarantee-title">Cancel anytime</div>
          <div className="pp-guarantee-sub">No long-term commitments. Cancel from Settings — access continues until period ends.</div>
        </div>
      </div>

      <div className="pp-faq">
        <h2 className="pp-faq-title">Frequently asked questions</h2>
        <div className="pp-faq-grid">
          <div className="pp-faq-item">
            <div className="pp-faq-q">What&#39;s included in Free?</div>
            <div className="pp-faq-a">The full live dashboard, watchlist, alerts log, and settings — no credit card required.</div>
          </div>
          <div className="pp-faq-item">
            <div className="pp-faq-q">How are signals refreshed?</div>
            <div className="pp-faq-a">Data is fetched from CoinGecko and Binance and recomputed server-side every hour across weekly, daily, and 4H timeframes.</div>
          </div>
          <div className="pp-faq-item">
            <div className="pp-faq-q">Can I switch plans?</div>
            <div className="pp-faq-a">Yes. Upgrades take effect immediately. Downgrades apply at the next billing cycle.</div>
          </div>
          <div className="pp-faq-item">
            <div className="pp-faq-q">What payment methods are accepted?</div>
            <div className="pp-faq-a">All major credit and debit cards via Stripe — a PCI-compliant payment processor.</div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default PricingPage;
